import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import theme from "theme";
import { platformStrip, sectionIds } from "content/site";
import { Container, Section, SectionTitle, SectionLead } from "components/marketing/primitives";
import PlatformLayerArt from "components/marketing/PlatformLayerArt";

/** Section background — fades must match so peek edges dissolve into the page */
const FADE_COLOR = theme.color.surface;

const CarouselWrap = styled.div`
	position: relative;
	margin: 0 auto;
	max-width: min(100%, 960px);
`;

const CarouselStage = styled.div`
	position: relative;
	border-radius: ${theme.radius.xl};
	box-shadow: ${theme.boxShadow.xl};
	border: 1px solid ${theme.color.border};
	background: ${FADE_COLOR};
`;

const CarouselViewport = styled.div`
	overflow-x: auto;
	overflow-y: hidden;
	scroll-snap-type: none;
	scroll-behavior: smooth;
	border-radius: ${theme.radius.xl};
	-webkit-overflow-scrolling: touch;

	scrollbar-width: thin;
	scrollbar-color: ${theme.color.greyD1} ${theme.color.lightD1};

	&::-webkit-scrollbar {
		height: 10px;
	}

	&::-webkit-scrollbar-track {
		background: ${theme.color.lightD1};
		border-radius: 0 0 ${theme.radius.xl} ${theme.radius.xl};
	}

	&::-webkit-scrollbar-thumb {
		background: ${theme.color.greyD1};
		border-radius: 5px;
	}

	@media (prefers-reduced-motion: reduce) {
		scroll-behavior: auto;
	}
`;

const CarouselTrack = styled.div`
	display: flex;
	flex-direction: row;
	align-items: stretch;
	padding-left: var(--carousel-pad, 24px);
	/*
	 * Block-level flex defaults to 100% parent width; some UAs then under-report horizontal
	 * overflow, so maxScroll never reaches the padded end — card 5 stays right-pinned.
	 * max-content + a real trailing flex item makes scrollWidth match the full strip.
	 */
	width: max-content;
	max-width: none;
	box-sizing: border-box;
	min-height: 1px;
`;

/** In-flow spacer (not padding) so the scroll range always includes right buffer. */
const TrackEndSpacer = styled.div`
	flex: 0 0 var(--carousel-pad-trailing, var(--carousel-pad, 24px));
	width: var(--carousel-pad-trailing, var(--carousel-pad, 24px));
	min-width: var(--carousel-pad-trailing, var(--carousel-pad, 24px));
	align-self: stretch;
	min-height: 1px;
	pointer-events: none;
`;

const Slide = styled.div`
	flex: 0 0 var(--slide-w, 300px);
	width: var(--slide-w, 300px);
	max-width: var(--slide-w, 300px);
	box-sizing: border-box;
	overflow: hidden;
	border-radius: ${theme.radius.lg};
	background: linear-gradient(180deg, ${theme.color.surface} 0%, ${theme.color.surfaceMuted} 100%);
	border: 1px solid ${theme.color.border};
	box-shadow: ${theme.boxShadow.m};

	/* Space between cards only — not before the trailing spacer (replaces flex gap). */
	& + & {
		margin-left: var(--carousel-gap, 16px);
	}
`;

const ArtArea = styled.div`
	background: ${theme.color.darkL1};
	padding: 1rem 0.75rem 1.25rem;
	position: relative;
	overflow: hidden;

	&::after {
		content: "";
		position: absolute;
		inset: 0;
		background: linear-gradient(
			145deg,
			rgba(0, 119, 204, 0.12) 0%,
			transparent 45%,
			rgba(255, 196, 0, 0.06) 100%
		);
		pointer-events: none;
	}

	& > * {
		position: relative;
		z-index: 1;
	}
`;

const SlideBody = styled.div`
	padding: 1.25rem 1.35rem 1.5rem;
	text-align: center;
`;

const StepBadge = styled.span`
	display: inline-block;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: ${theme.color.primary};
	margin-bottom: 0.35rem;
`;

const NodeTitle = styled.h3`
	font-family: ${theme.fontFamily.sans};
	font-size: 1.35rem;
	font-weight: 800;
	color: ${theme.color.dark};
	margin: 0 0 0.5rem;
	letter-spacing: -0.02em;
`;

const Blurb = styled.p`
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	color: ${theme.color.greyD2};
	margin: 0;
	line-height: 1.5;
	max-width: 28rem;
	margin-left: auto;
	margin-right: auto;
`;

const EdgeFade = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	width: clamp(56px, 18%, 120px);
	z-index: 2;
	pointer-events: none;
	border-radius: ${(p) =>
		p.$side === "left" ? `${theme.radius.xl} 0 0 ${theme.radius.xl}` : `0 ${theme.radius.xl} ${theme.radius.xl} 0`};
`;

const FadeLeft = styled(EdgeFade)`
	left: 0;
	background: linear-gradient(
		90deg,
		${FADE_COLOR} 0%,
		${FADE_COLOR} 15%,
		rgba(255, 255, 255, 0.55) 55%,
		transparent 100%
	);
`;

const FadeRight = styled(EdgeFade)`
	right: 0;
	background: linear-gradient(
		270deg,
		${FADE_COLOR} 0%,
		${FADE_COLOR} 15%,
		rgba(255, 255, 255, 0.55) 55%,
		transparent 100%
	);
`;

const ArrowBtn = styled.button`
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	z-index: 4;
	width: 44px;
	height: 44px;
	border-radius: 50%;
	border: 1px solid ${theme.color.border};
	background: rgba(255, 255, 255, 0.95);
	box-shadow: ${theme.boxShadow.m};
	color: ${theme.color.dark};
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease;

	&:hover:not(:disabled) {
		background: ${theme.color.primary};
		color: #fff;
		border-color: ${theme.color.primary};
	}

	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 2px;
	}

	&:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}

	@media (max-width: 480px) {
		width: 38px;
		height: 38px;
	}
`;

const ArrowPrev = styled(ArrowBtn)`
	left: clamp(4px, 2vw, 10px);
`;

const ArrowNext = styled(ArrowBtn)`
	right: clamp(4px, 2vw, 10px);
`;

const DotsRow = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 0.45rem;
	margin-top: 1.35rem;
	flex-wrap: wrap;
`;

const Dot = styled.button`
	width: ${(p) => (p.$active ? "26px" : "10px")};
	height: 10px;
	border-radius: 999px;
	border: none;
	padding: 0;
	cursor: pointer;
	background: ${(p) => (p.$active ? theme.color.primary : theme.color.lightD2)};
	transition: width 0.22s ease, background 0.22s ease;
	flex-shrink: 0;

	&:hover {
		background: ${(p) => (p.$active ? theme.color.primary : theme.color.greyD1)};
	}

	&:focus-visible {
		outline: 2px solid ${theme.color.primary};
		outline-offset: 3px;
	}
`;

const SrOnly = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`;

const LiveRegion = styled.div`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
`;

const CarouselHint = styled.p`
	text-align: center;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.8125rem;
	color: ${theme.color.grey};
	margin: 0.75rem 0 0;
	line-height: 1.4;
`;

const GAP = 80;

/** Target scrollLeft to put `slide` horizontal center on viewport center (paint-accurate). */
function scrollLeftToCenterSlide(viewport, slide) {
	const vpRect = viewport.getBoundingClientRect();
	const slideRect = slide.getBoundingClientRect();
	const slideCenter = slideRect.left + slideRect.width / 2;
	const vpCenter = vpRect.left + vpRect.width / 2;
	return viewport.scrollLeft + (slideCenter - vpCenter);
}

function getClosestSlideIndex(viewportEl) {
	if (!viewportEl) return 0;
	const slides = viewportEl.querySelectorAll("[data-slide]");
	if (!slides.length) return 0;
	const vpRect = viewportEl.getBoundingClientRect();
	const centerX = vpRect.left + vpRect.width / 2;
	let best = 0;
	let bestDist = Infinity;
	slides.forEach((slide, i) => {
		const r = slide.getBoundingClientRect();
		const c = r.left + r.width / 2;
		const d = Math.abs(c - centerX);
		if (d < bestDist) {
			bestDist = d;
			best = i;
		}
	});
	return best;
}

export default function PlatformStrip() {
	const layers = platformStrip.layers;
	const viewportRef = useRef(null);
	const trackRef = useRef(null);
	const activeRef = useRef(0);
	const scrollToIndexRef = useRef(() => {});
	const scrollOpIdRef = useRef(0);
	const [active, setActive] = useState(0);

	useEffect(() => {
		activeRef.current = active;
	}, [active]);

	const layoutCarousel = useCallback(() => {
		const vp = viewportRef.current;
		const track = trackRef.current;
		if (!vp || !track) return;
		const w = vp.clientWidth;
		if (w <= 0) return;
		const slideW = Math.min(480, Math.max(280, Math.round(w * 0.62)));
		vp.style.setProperty("--slide-w", `${slideW}px`);
		vp.style.setProperty("--carousel-gap", `${GAP}px`);
		const provisionalPad = Math.max(24, Math.ceil((w - slideW) / 2));
		vp.style.setProperty("--carousel-pad", `${provisionalPad}px`);
		/* Trailing buffer mirrors leading pad + slack for scrollbar / subpixel */
		vp.style.setProperty(
			"--carousel-pad-trailing",
			`${provisionalPad + Math.max(24, Math.ceil(GAP / 2))}px`
		);

		requestAnimationFrame(() => {
			const slide = track.querySelector("[data-slide]");
			if (!slide || !vp.isConnected) return;
			const measured = slide.getBoundingClientRect().width;
			const pad = Math.max(24, Math.ceil((vp.clientWidth - measured) / 2));
			vp.style.setProperty("--carousel-pad", `${pad}px`);
			const trailing = pad + Math.max(24, Math.ceil(GAP / 2));
			vp.style.setProperty("--carousel-pad-trailing", `${trailing}px`);
		});
	}, []);

	const remeasureAndRecenter = useCallback(() => {
		layoutCarousel();
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				scrollToIndexRef.current(activeRef.current);
			});
		});
	}, [layoutCarousel]);

	useLayoutEffect(() => {
		layoutCarousel();
	}, [layoutCarousel]);

	useEffect(() => {
		const vp = viewportRef.current;
		if (!vp) return;
		const ro = new ResizeObserver(() => remeasureAndRecenter());
		ro.observe(vp);
		window.addEventListener("resize", remeasureAndRecenter);
		return () => {
			ro.disconnect();
			window.removeEventListener("resize", remeasureAndRecenter);
		};
	}, [remeasureAndRecenter]);

	const updateActiveFromScroll = useCallback(() => {
		const idx = getClosestSlideIndex(viewportRef.current);
		setActive(Math.min(Math.max(0, idx), layers.length - 1));
	}, [layers.length]);

	useEffect(() => {
		const el = viewportRef.current;
		if (!el) return;
		const onScroll = () => window.requestAnimationFrame(updateActiveFromScroll);
		el.addEventListener("scroll", onScroll, { passive: true });
		updateActiveFromScroll();
		return () => el.removeEventListener("scroll", onScroll);
	}, [updateActiveFromScroll]);

	const scrollToIndex = useCallback((index) => {
		const vp = viewportRef.current;
		const track = trackRef.current;
		if (!vp || !track) return;
		const clamped = Math.min(Math.max(0, index), layers.length - 1);
		const slides = track.querySelectorAll("[data-slide]");
		const slide = slides[clamped];
		if (!slide) return;

		const reduceMotion =
			typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		const alignInstant = () => {
			let lastLeft = -1;
			for (let i = 0; i < 16; i++) {
				const maxScroll = Math.max(0, vp.scrollWidth - vp.clientWidth);
				const target = scrollLeftToCenterSlide(vp, slide);
				const left = Math.max(0, Math.min(maxScroll, Math.round(target)));
				if (left === lastLeft) break;
				lastLeft = left;
				vp.scrollLeft = left;
				const vpRect = vp.getBoundingClientRect();
				const sr = slide.getBoundingClientRect();
				const err = sr.left + sr.width / 2 - (vpRect.left + vpRect.width / 2);
				if (Math.abs(err) < 0.5) break;
			}
		};

		const applyCenterScroll = (behavior) => {
			const maxScroll = Math.max(0, vp.scrollWidth - vp.clientWidth);
			const targetScroll = scrollLeftToCenterSlide(vp, slide);
			const left = Math.max(0, Math.min(maxScroll, Math.round(targetScroll)));
			vp.scrollTo({ left, behavior });
		};

		if (reduceMotion) {
			applyCenterScroll("auto");
			requestAnimationFrame(alignInstant);
		} else {
			const opId = ++scrollOpIdRef.current;
			applyCenterScroll("smooth");
			const finalize = () => {
				if (scrollOpIdRef.current !== opId) return;
				alignInstant();
			};
			vp.addEventListener("scrollend", finalize, { once: true });
			window.setTimeout(finalize, 700);
		}

		setActive(clamped);
	}, [layers.length]);

	scrollToIndexRef.current = scrollToIndex;

	const onKeyDown = useCallback(
		(e) => {
			if (e.key === "ArrowRight") {
				e.preventDefault();
				scrollToIndex(active + 1);
			} else if (e.key === "ArrowLeft") {
				e.preventDefault();
				scrollToIndex(active - 1);
			}
		},
		[active, scrollToIndex]
	);

	const current = layers[active];

	return (
		<Section id={sectionIds.platform} aria-labelledby="platform-heading" $alt>
			<Container>
				<SectionTitle id="platform-heading">{platformStrip.title}</SectionTitle>
				<SectionLead>{platformStrip.lead}</SectionLead>

				<CarouselWrap>
					<SrOnly id="platform-carousel-label">
						Platform layers — scroll the panel, use arrows, or dots to change step
					</SrOnly>
					<LiveRegion aria-live="polite" aria-atomic="true">
						{current ? `Step ${active + 1} of ${layers.length}: ${current.label}` : ""}
					</LiveRegion>

					<CarouselStage>
						<FadeLeft $side="left" aria-hidden />
						<FadeRight $side="right" aria-hidden />

						<ArrowPrev
							type="button"
							aria-label="Previous platform step"
							disabled={active <= 0}
							onClick={() => scrollToIndex(active - 1)}
						>
							<MdChevronLeft size={26} />
						</ArrowPrev>
						<ArrowNext
							type="button"
							aria-label="Next platform step"
							disabled={active >= layers.length - 1}
							onClick={() => scrollToIndex(active + 1)}
						>
							<MdChevronRight size={26} />
						</ArrowNext>

						<CarouselViewport
							ref={viewportRef}
							role="region"
							aria-roledescription="carousel"
							aria-labelledby="platform-carousel-label"
							tabIndex={0}
							onKeyDown={onKeyDown}
						>
							<CarouselTrack ref={trackRef} data-carousel-track>
								{layers.map((layer, i) => (
									<Slide
										key={layer.key}
										data-slide
										role="group"
										aria-roledescription="slide"
										aria-label={`${i + 1} of ${layers.length}: ${layer.label}`}
										aria-hidden={i !== active}
									>
										<ArtArea>
											<PlatformLayerArt layerKey={layer.key} title={layer.artTitle} />
										</ArtArea>
										<SlideBody>
											<StepBadge>
												{i + 1} / {layers.length}
											</StepBadge>
											<NodeTitle>{layer.label}</NodeTitle>
											<Blurb>{layer.blurb}</Blurb>
										</SlideBody>
									</Slide>
								))}
								<TrackEndSpacer aria-hidden data-carousel-end-spacer />
							</CarouselTrack>
						</CarouselViewport>
					</CarouselStage>

					<DotsRow role="tablist" aria-label="Platform steps">
						{layers.map((layer, i) => (
							<Dot
								key={layer.key}
								type="button"
								role="tab"
								aria-selected={i === active}
								aria-label={`Show ${layer.label}`}
								$active={i === active}
								onClick={() => scrollToIndex(i)}
							/>
						))}
					</DotsRow>
					<CarouselHint>
						Drag or scroll the strip · Arrows · Dots · Arrow keys when the carousel is focused
					</CarouselHint>
				</CarouselWrap>
			</Container>
		</Section>
	);
}
